import React, {useState} from "react";
import {Button, Checkbox, Input, message, Modal, Select, Table, Upload} from "antd";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";
import {DocumentModal} from "../../../documents/documentModal";
import {UploadOutlined} from "@ant-design/icons";
import {useFormik} from "formik";
import * as Yup from "yup";

const {Option} = Select;

export const ContractsTab = ({subCompanyId, recall, setRecall}) => {
    const stores = useStore();
    const [
        agreementsLoading,
        agreements,
        setAgreements,
        setAgreementsLoading,
        fetchAgreements
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: () => stores.contractStore.getContractsBySubCompanyId({subCompanyId}),
        dependencies: [subCompanyId, recall]
    });

    const [isModalVisible, setIsModalVisible] = React.useState(false);
    const [isUploadModalVisible, setIsUploadModalVisible] = React.useState(false);
    const [isTemplateModalVisible, setIsTemplateModalVisible] = React.useState(false);
    const [documentUrl, setDocumentUrl] = React.useState(null);
    const [contractId, setContractId] = useState(null);

    const [
        templatesLoading,
        templateNames,
        setTemplateNames,
        setTemplatesLoading,
        fetchTemplateNames
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: async () => {
            const templates = await stores.contractStore.getContractTemplates();
            return templates.filter(template => !template.isNeedForApproval);
        },
        dependencies: [stores.contractStore]
    });

    const handleShowDocument = contract => {
        const filePath = contract?.files?.[0]?.path;
        if (filePath) {
            setDocumentUrl(filePath);
            setContractId(contract._id);
            setIsModalVisible(true);
        } else {
            console.error("Contract file path is not found!");
        }
    };

    const handleCancelModal = () => {
        setIsModalVisible(false);
        setDocumentUrl(null);
        setContractId(null);
        setRecall();
    };

    const uploadFormik = useFormik({
        initialValues: {
            selectedFile: null,
            selectedContractName: null
        },
        validationSchema: Yup.object({
            selectedFile: Yup.mixed().required("Lütfen bir dosya seçin!"),
            selectedContractName: Yup.string().nullable().required("Lütfen bir sözleşme türü seçin!")
        }),
        onSubmit: async values => {
            try {
                const formData = new FormData();
                formData.append("file", values.selectedFile);
                formData.append("contractName", values.selectedContractName);
                const response = await stores.contractStore.createContract({
                    subCompanyId,
                    formData
                });
                if (response?.status === 201) {
                    message.success("Dosya başarıyla yüklendi!");
                    setIsUploadModalVisible(false);
                    await fetchAgreements();
                    uploadFormik.resetForm();
                } else {
                    message.error("Dosya yüklenirken bir hata oluştu.");
                }
            } catch (error) {
                console.log(error);
                message.error("Dosya yüklenirken bir hata oluştu.", error);
            }
        }
    });

    return (
        <div>
            {templateNames.length > 0 || false && ( // TODO: phase 2, check comments https://vigo-tech.atlassian.net/browse/TM1-79
                <Button
                    type="primary"
                    style={{marginBottom: 16}}
                    onClick={() => {
                        uploadFormik.resetForm();
                        setIsUploadModalVisible(true);
                    }}
                >
                    Yeni Sözleşme Yükle
                </Button>
            )}

            <Table loading={agreementsLoading} dataSource={agreements?.data} rowKey="_id">
                <Table.Column title="Sözleşme Adı" dataIndex="displayName" />
                <Table.Column
                    title="Belge"
                    render={(text, record) => (
                        <Button onClick={() => handleShowDocument(record)}>Görüntüle</Button>
                    )}
                />
                <Table.Column
                    title="Onay Durumu"
                    render={(text, record) =>
                        record.isNeedForApproval
                            ? record.isApproved === null
                                ? "Beklemede"
                                : record.isApproved
                                ? "Onaylandı"
                                : "Reddedildi"
                            : "Onaya Gerek Yok"
                    }
                />
            </Table>

            {documentUrl && (
                <DocumentModal
                    visibility={isModalVisible}
                    onCancel={handleCancelModal}
                    documentUrl={documentUrl}
                    title="Belge Görüntüleyici"
                    isNeedForApproval={
                        agreements?.data.find(c => c._id === contractId)
                            ?.isNeedForApproval
                    }
                    isApproved={
                        agreements?.data.find(c => c._id === contractId)
                            ?.isApproved
                    }
                    onApprove={() => {
                        const payload = {
                            contractId: contractId,
                            isApproved: true,
                        };
                        stores.contractStore.changeApprovalStatus(payload);
                        setIsModalVisible(false);
                        fetchAgreements();
                        setRecall();
                    }}
                    isApproveButtonDisabled={true}
                />
            )}

            <Modal
                title="Yeni Sözleşme Yükle"
                visible={isUploadModalVisible}
                onCancel={() => setIsUploadModalVisible(false)}
                onOk={uploadFormik.handleSubmit}
                okText="Yükle"
                cancelText="İptal"
            >
                <Select
                    style={{width: "100%", marginBottom: 16}}
                    placeholder="Sözleşme türü seçin"
                    onChange={value => uploadFormik.setFieldValue("selectedContractName", value)}
                    value={uploadFormik.values.selectedContractName}
                >
                    {templateNames.map(template => (
                        <Option key={template.contractName} value={template.contractName}>
                            {template.displayName}
                        </Option>
                    ))}
                </Select>

                {uploadFormik.touched.selectedContractName &&
                    uploadFormik.errors.selectedContractName && (
                        <div style={{color: "red", marginBottom: 16}}>
                            {uploadFormik.errors.selectedContractName}
                        </div>
                    )}

                <Upload
                    beforeUpload={file => {
                        uploadFormik.setFieldValue("selectedFile", file);
                        return false;
                    }}
                    maxCount={1}
                    fileList={
                        uploadFormik.values.selectedFile ? [uploadFormik.values.selectedFile] : []
                    }
                    onRemove={() => uploadFormik.setFieldValue("selectedFile", null)}
                >
                    <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                </Upload>
                {uploadFormik.touched.selectedFile && uploadFormik.errors.selectedFile && (
                    <div style={{color: "red", marginTop: 8}}>
                        {uploadFormik.errors.selectedFile}
                    </div>
                )}
            </Modal>
        </div>
    );
};
